import { Component, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, ChangeDetectionStrategy, inject, viewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { StaffById } from '../../interfaces/staff.interface';
import { StaffService } from '../../services/staff.service';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass, TitleCasePipe } from '@angular/common';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { Store } from '@ngrx/store';
import { SkeletonModule } from 'primeng/skeleton';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AppState } from '@fitness-force/state';
import { setTheme } from '@fitness-force/state';
import { ImageCropDialogComponent } from '@fitness-force/common/crop';
import { StaffChangePasswordComponent } from '@fitness-force/shared';
import { THEME_CONFIG_NEW} from '@fitness-force/constants';
import { SelectModule } from 'primeng/select';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { PrimeNG } from 'primeng/config';
import { AuthSevices, ToastMessageService } from '@fitness-force/api';


@Component({
  selector: 'staff-profile',
  templateUrl: './staff-details.component.html',
  styleUrls: ['./staff-details.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    AvatarModule,
    ButtonModule,
    InputTextModule,
    FileUploadModule,
    SkeletonModule,
    TranslateModule,
    NgxFileDropModule,
    SelectModule,
    ShortNamePipe,
    StaffChangePasswordComponent,
    DateTimeFormatPipe,
    NgClass,
    TitleCasePipe
],
  providers: [DialogService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StaffDetailsComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private staffService = inject(StaffService);
  private toastMessageService = inject(ToastMessageService);
  private authService = inject(AuthSevices);
  private store = inject<Store<AppState>>(Store);
  private dialogService = inject(DialogService);
  private cdr = inject(ChangeDetectorRef);
  private primeng = inject(PrimeNG);

  private destroy$ = new Subject<void>();
  active1 = 0;
  themePreviewActive1 = 0;
  staffDetails: StaffById | null = null;
  bioForm: FormGroup = new FormGroup({});
  isLoading = false;
  isThemeChanging = false;
  refCropper?: DynamicDialogRef | null;
  showChangePassword:boolean = false;
  themeChangeTimer: any = null;
  readonly uploadClientImage = viewChild.required<ElementRef>('uploadClientImage');
  
  themes:any = THEME_CONFIG_NEW;
  selectedTheme: any;
  selectedColor: any;

  constructor() {
    this.initializeForms();
  }

  ngOnInit(): void {
    // Load saved theme or default to light theme
    const savedTheme = localStorage.getItem('selected-theme');
    if (savedTheme) {
      try {
        const parsedTheme = JSON.parse(savedTheme);
        this.selectedTheme = this.themes.find((theme:any) => 
          theme.themeMode === parsedTheme.themeMode 
        );
        if (this.selectedTheme) {
          this.selectedTheme.selectedColor= parsedTheme.selectedColor;
          this.selectedColor = parsedTheme.selectedColor;
          this.selectedTheme.selectedTheme = parsedTheme.selectedTheme;
          // const selectedColor = this.selectedTheme.themes[0].colors.find((color: any) => color === this.selectedTheme.selectedColor);
          // if (selectedColor) {
          //   this.selectedColor = selectedColor;
          // }
        }
      } catch (e) {
        console.error('Error parsing saved theme:', e);
        this.selectedTheme = this.themes.find((theme:any) => theme.themeMode === 'light')?.themes[0];
      }
    } else {
      this.selectedTheme = this.themes.find((theme:any) => theme.themeMode === 'light')?.themes[0];
    }
    this.loadStaffDetails();
  }

  private initializeForms(): void {
    this.bioForm = this.fb.group({
        public_bio_video_url: ['', [Validators.pattern('https?://.+')]],
        public_bio_description: ['', [Validators.maxLength(500)]],
        public_bio_achievements: ['', [Validators.maxLength(1000)]],
        image: ['']
    });
  }


  private loadStaffDetails(): void {
    var staffId = this.authService.loggedInStaffId;
    this.isLoading = true;
    this.staffService.getStaffDetails(staffId) // Replace with actual staff ID
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (staff) => {
          this.staffDetails = staff;
          this.populateForms();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.toastMessageService.showErrorMessage('Failed to load staff details');
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private populateForms(): void {
    if (this.staffDetails) {
      this.bioForm.patchValue({
        public_bio_video_url: this.staffDetails.public_bio ? this.staffDetails.public_bio.public_bio_video_url : '',
        public_bio_description: this.staffDetails.public_bio ? this.staffDetails.public_bio.public_bio_description_primary_language || this.staffDetails.public_bio.public_bio_description_secondary_language : '',
        public_bio_achievements: this.staffDetails.public_bio ? this.staffDetails.public_bio.public_bio_achievements_primary_language || this.staffDetails.public_bio.public_bio_achievements_secondary_language : ''
      });
    }
  }

  onUpdatePicture(): void {
    let patchObject: Array<{ op: string, path: string, value: any }> = [];
    patchObject.push({ op: "replace", path: "/FFPicture", value: this.bioForm.value.image.replace('data:image/png;base64,', '') });
      this.staffService.patchStaffDetails(patchObject)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.staffDetails!.image = this.bioForm.value.image;
            this.toastMessageService.showSuccess('Profile picture updated successfully');
          },
          error: () => {
            this.toastMessageService.showErrorMessage('Failed to update profile picture');
          }
        });
  }

  selectDefaultTheme(selectedTheme: string): void {
    if (this.isThemeChanging) return;
    const theme = this.themes.find((theme:any) => theme.themeMode === selectedTheme)!;
     this.onThemeChange(theme);
  }

  selectThemeColor(selectedThemeName: string, selectedColor: string): void {
    if (this.isThemeChanging) return;
    const selectedTheme = this.themes.find((theme:any) => theme.themeMode === selectedThemeName)!;
    const color = selectedTheme.themes[0].colors.find((color: any) => color === selectedColor)!;
    this.selectedColor = selectedColor;
    // Create a new object to avoid mutating a frozen object
    const themeToApply = {
      ...selectedTheme,
      selectedColor: color,
      selectedTheme: selectedTheme.themes[0].themeName
    };
    this.onThemeChange(themeToApply);
  }

  onThemeChange(theme: any): void {
    if (this.isThemeChanging) return;
    
    if (this.themeChangeTimer) {
      clearTimeout(this.themeChangeTimer);
    }
    
    this.isThemeChanging = true;
    this.cdr.markForCheck();
    
    this.selectedTheme = theme;
    localStorage.setItem('selected-theme', JSON.stringify(theme));

    // Dispatch theme to store - ThemeEffects will handle the actual theme application
    this.store.dispatch(setTheme({ theme: theme }));
    
    // Set a timer to hide the loading indicator
    this.themeChangeTimer = setTimeout(() => {
      this.isThemeChanging = false;
      this.cdr.markForCheck();
      this.themeChangeTimer = null;
    }, 1000);
  }

  onSaveChanges(): void {
    if (this.bioForm.valid) {
      const bioData = this.bioForm.value;
      let publicBioObj: Array<{ op: string, path: string, value: any }> = [];
      let patchObject: Array<{ op: string, path: string, value: any }> = [];
      if (bioData.public_bio_video_url !== this.staffDetails?.public_bio?.public_bio_video_url) {
        publicBioObj.push({ op: "replace", path: "/PublicBioVideoUrl", value: bioData.public_bio_video_url });
      }
      if (bioData.public_bio_description !== this.staffDetails?.public_bio?.public_bio_description_primary_language && bioData.public_bio_description !== this.staffDetails?.public_bio?.public_bio_description_secondary_language) {
        publicBioObj.push({ op: "replace", path: "/PublicBioDescriptionPrimaryLanguage", value: bioData.public_bio_description });
      }
      if (bioData.public_bio_achievements !== this.staffDetails?.public_bio?.public_bio_achievements_primary_language && bioData.public_bio_achievements !== this.staffDetails?.public_bio?.public_bio_achievements_secondary_language) {
        publicBioObj.push({ op: "replace", path: "/PublicBioAchievementsPrimaryLanguage", value: bioData.public_bio_achievements });
      }

      if(publicBioObj.length > 0){
      patchObject.push({ op: "replace", path: "/PublicBio", value: publicBioObj });
      }

      if (bioData.image) {
        patchObject.push({ op: "replace", path: "/FFPicture", value: bioData.image.replace('data:image/png;base64,', '') });
      }

      if(patchObject.length > 0) {
      this.staffService.patchStaffDetails(patchObject)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.toastMessageService.showSuccess('Profile updated successfully');
          },
          error: () => {
            this.toastMessageService.showErrorMessage('Failed to update profile');
          }
        });
      }
    }
    else {
      Object.keys(this.bioForm.controls).forEach(key => {
        this.bioForm.get(key)?.markAsDirty();
      });
    }
  }

  handleFileSelect(files: NgxFileDropEntry[]) {
    let file: NgxFileDropEntry = files[0];
    if (file.fileEntry.isFile) {
      const fileEntry = file.fileEntry as FileSystemFileEntry;
      fileEntry.file((file: File) => {
        this.showCropper(file);
      });
    }
  }
  
  showCropper(imageFile: any) {
    let header: string = 'common.IMAGE_CROP.STAFF_IMAGE';
    this.refCropper = this.dialogService.open(ImageCropDialogComponent, {
      showHeader: false,
      baseZIndex: 10000,
      width: '70%',
      contentStyle: { 'max-height': '500px', 'max-width': '800px' },
      data: { imageFile, header },
    });
    if (this.refCropper) {
      this.refCropper.onClose.subscribe((croppedImage: any) => {
        if (croppedImage) {
          this.bioForm.get('image')?.setValue(croppedImage.image);
          this.onUpdatePicture();
          this.cdr.detectChanges();
        }
      });
    }
  }

  setCloseChangePassword() {
    this.showChangePassword = false;
  }

  onPasswordChanged(event: any) {
    if (event.ischanged) {
      this.staffDetails!.password_last_changed = new Date().toISOString();
      this.cdr.detectChanges();
    }
  }

  getImage() {
    if(this.staffDetails?.image || this.bioForm.get('image')?.value)
    {
      return {path: this.staffDetails?.image || this.bioForm.get('image')?.value, isImage: true};
    }
    else
    {
      return {path: '', isImage: false};
    }
  }

  ngOnDestroy(): void {
    // Clean up any timers to prevent memory leaks
    if (this.themeChangeTimer) {
      clearTimeout(this.themeChangeTimer);
    }
    
    this.destroy$.next();
    this.destroy$.complete();
  }
} 